/**
 * Ritual Club — assets/ritual-club.js
 *
 * Vanilla ES6, no globals: every behavior lives inside a custom element or a
 * small class instantiated once from the bottom of this file. Nothing is
 * attached to `window` except the two custom element definitions, which is
 * how the platform expects them to be registered.
 *
 * Deliberately NOT handled here: the FAQ accordion. Minimog already ships a
 * global <collapsible-tab> custom element (see sections/page-faqs.liquid)
 * and this section reuses that exact element — writing a second accordion
 * controller here would double-bind the same DOM nodes and break Minimog's
 * own open/close animation. Reusing the theme's existing element is the
 * correct call, not a missing feature.
 */

(() => {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * <ritual-plan-card>
   * Wraps one subscription card: frequency select, price display,
   * add-to-cart form, loading state, error handling, checkout redirect.
   * Progressive enhancement: the <form> underneath has a real action/method,
   * so if this script fails to load or throws, the browser still submits
   * a normal POST to /cart/add and the shopper lands on the cart page —
   * degraded, but never broken.
   */
  class RitualPlanCard extends HTMLElement {
    connectedCallback() {
      this._form = this.querySelector('[data-ritual-form]');
      this._select = this.querySelector('[data-ritual-freq-select]');
      this._sellingPlanInput = this.querySelector('[data-ritual-selling-plan]');
      this._priceNow = this.querySelector('[data-ritual-price-now]');
      this._priceWas = this.querySelector('[data-ritual-price-was]');
      this._freqLabel = this.querySelector('[data-ritual-freq-label]');
      this._message = this.querySelector('[data-ritual-message]');
      this._submitBtn = this.querySelector('[data-ritual-submit]');
      this._submitBtnText = this.querySelector('[data-ritual-button-text]');

      const dataNode = this.querySelector('[data-ritual-plan-data]');
      this._plans = [];
      if (dataNode) {
        try {
          this._plans = JSON.parse(dataNode.textContent);
        } catch (err) {
          this._plans = [];
        }
      }

      if (this._select && this._plans.length > 1) {
        this._select.addEventListener('change', () => this._applyPlan(this._select.value));
      }

      if (this._form) {
        this._form.addEventListener('submit', (event) => this._handleSubmit(event));
      }
    }

    _findPlan(id) {
      return this._plans.find((plan) => String(plan.id) === String(id));
    }

    _applyPlan(sellingPlanId) {
      const plan = this._findPlan(sellingPlanId);
      if (!plan) return;

      if (this._sellingPlanInput) this._sellingPlanInput.value = plan.id;
      if (this._priceNow) this._priceNow.textContent = plan.priceFormatted;

      if (this._priceWas) {
        if (plan.hasSavings) {
          this._priceWas.textContent = plan.comparePriceFormatted;
          this._priceWas.hidden = false;
        } else {
          this._priceWas.hidden = true;
        }
      }

      if (this._freqLabel) {
        this._freqLabel.textContent = this._freqLabel.dataset.template
          ? this._freqLabel.dataset.template.replace('{{plan}}', plan.name)
          : plan.name;
      }
    }

    _setLoading(isLoading) {
      if (!this._submitBtn) return;
      this._submitBtn.disabled = isLoading;
      this._submitBtn.setAttribute('aria-busy', String(isLoading));
    }

    _showMessage(text, state) {
      if (!this._message) return;
      this._message.textContent = text;
      this._message.hidden = false;
      this._message.dataset.state = state;
    }

    _clearMessage() {
      if (!this._message) return;
      this._message.hidden = true;
      this._message.textContent = '';
    }

    async _handleSubmit(event) {
      event.preventDefault();

      if (this._submitBtn && this._submitBtn.disabled) return;

      this._clearMessage();
      this._setLoading(true);

      const formData = new FormData(this._form);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        const payload = await response.json();

        if (!response.ok) {
          const description =
            (payload && (payload.description || payload.message)) ||
            'This item could not be added to your cart. Please try again.';
          throw new Error(description);
        }

        this._showMessage('Added — redirecting to checkout…', 'success');
        window.location.href = '/checkout';
      } catch (error) {
        this._setLoading(false);
        this._showMessage(
          error && error.message
            ? error.message
            : 'Something went wrong adding this to your cart. Please try again.',
          'error'
        );
        if (this._message) this._message.focus?.();
      }
    }
  }

  /**
   * <ritual-sticky-cta>
   * Shows a fixed bottom bar on small screens once the hero has scrolled
   * out of view, so the "Join the Ritual Club" action stays reachable
   * without following the shopper down the whole page.
   */
  class RitualStickyCta extends HTMLElement {
    connectedCallback() {
      const heroSelector = this.getAttribute('data-watch');
      const hero = heroSelector ? document.querySelector(heroSelector) : null;
      if (!hero || !('IntersectionObserver' in window)) return;

      this._observer = new IntersectionObserver(
        (entries) => {
          const heroVisible = entries[0].isIntersecting;
          this.setAttribute('data-visible', String(!heroVisible));
        },
        { rootMargin: '-10% 0px 0px 0px' }
      );
      this._observer.observe(hero);
    }

    disconnectedCallback() {
      this._observer?.disconnect();
    }
  }

  customElements.define('ritual-plan-card', RitualPlanCard);
  customElements.define('ritual-sticky-cta', RitualStickyCta);

  /**
   * Optional GSAP scroll-reveal, only runs when:
   *  - the merchant enabled it in the Theme Editor (section sets the
   *    [data-ritual-animations-ready] attribute only in that case), and
   *  - GSAP + ScrollTrigger loaded successfully, and
   *  - the visitor has not requested reduced motion.
   * Every element it targets is fully visible by default in the CSS
   * (see ritual-club.css), so a slow/failed GSAP load never hides content —
   * it only upgrades an already-complete page.
   */
  function initScrollReveal() {
    const root = document.querySelector('[data-ritual-animations-ready]');
    if (!root || REDUCED_MOTION) return;
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;

    window.gsap.registerPlugin(window.ScrollTrigger);
    const targets = root.querySelectorAll('[data-animate]');

    targets.forEach((el, index) => {
      window.gsap.fromTo(
        el,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          delay: (index % 5) * 0.06,
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            once: true,
          },
        }
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollReveal);
  } else {
    initScrollReveal();
  }
})();